import { useState, useCallback, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { compact } from 'lodash';
import { useRouter } from '@uirouter/react';

import { Registry } from '@/react/portainer/registries/types/registry';
import { useEnvironmentRegistries } from '@/react/portainer/environments/queries/useEnvironmentRegistries';
import { useUpdateGitStack } from '@/react/portainer/gitops/queries/useUpdateGitStack';
import { useUpdateGitStackSettings } from '@/react/portainer/gitops/queries/useUpdateGitStackSettings';
import { GitStackPayload } from '@/react/common/stacks/types';
import {
  GitFormModel,
  AutoUpdateModel,
  AutoUpdateResponse,
} from '@/react/portainer/gitops/types';
import { RelativePathModel } from '@/react/portainer/gitops/RelativePathFieldset/types';
import {
  parseAutoUpdateResponse,
  transformAutoUpdateViewModel,
} from '@/react/portainer/gitops/AutoUpdateFieldset/utils';
import { confirmStackUpdate } from '@/react/common/stacks/common/confirm-stack-update';
import { confirmEnableTLSVerify } from '@/react/portainer/gitops/utils';
import {
  baseStackWebhookUrl,
  createWebhookId,
} from '@/portainer/helpers/webhookHelper';
import { InfoPanel } from '@/react/portainer/gitops/InfoPanel';
import { AutoUpdateFieldset } from '@/react/portainer/gitops/AutoUpdateFieldset';
import { TimeWindowDisplay } from '@/react/portainer/gitops/TimeWindowDisplay';
import { RefField } from '@/react/portainer/gitops/RefField';
import { AuthFieldset } from '@/react/portainer/gitops/AuthFieldset';
import { RelativePathFieldset } from '@/react/portainer/gitops/RelativePathFieldset/RelativePathFieldset';
import { MultiRegistrySelectFieldset } from '@/react/components/form-components/MultiRegistrySelectFieldset/MultiRegistrySelectFieldset';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';

import { Icon } from '@@/Icon';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { Button } from '@@/buttons/Button';
import { SwitchField } from '@@/form-components/SwitchField/SwitchField';
import { FormSection } from '@@/form-components/FormSection/FormSection';
import { StackEnvironmentVariablesPanel } from '@@/form-components/EnvironmentVariablesFieldset/StackEnvironmentVariablesPanel';
import { Tooltip } from '@@/Tip/Tooltip/Tooltip';

interface StackRedeployGitFormModel extends GitFormModel {
  RefName?: string;
  Env: Array<{ name: string; value: string }>;
  Option: {
    Prune: boolean;
  };
  PullImage: boolean;
  SelectedRegistries: Registry[];
  isEdit?: boolean;
  isAuthEdit?: boolean;
  TLSSkipVerify?: boolean;
}

interface StackRedeployGitFormProps {
  model: {
    URL: string;
    ReferenceName: string;
    ConfigFilePath: string;
    ConfigHash: string;
    TLSSkipVerify: boolean;
  };
  stack: {
    Id: number;
    EndpointId: number;
    Type: number;
    Env: Array<{ name: string; value: string }>;
    Option?: {
      Prune: boolean;
    };
    AdditionalFiles?: string[];
    AutoUpdate?: AutoUpdateResponse;
    GitConfig?: {
      Authentication?: {
        Username?: string;
        Password?: string;
        GitCredentialID?: number;
      };
    };
    Registries?: number[];
  };
  endpoint: {
    apiVersion: number;
    Id: number;
  };
}

interface StackRedeployGitFormState {
  inProgress: boolean;
  redeployInProgress: boolean;
  showConfig: boolean;
  hasUnsavedChanges: boolean;
  baseWebhookUrl: string;
  webhookId: string;
}

const defaultState: StackRedeployGitFormState = {
  inProgress: false,
  redeployInProgress: false,
  showConfig: false,
  hasUnsavedChanges: false,
  baseWebhookUrl: baseStackWebhookUrl(),
  webhookId: createWebhookId(),
};

export function StackRedeployGitForm({
  model,
  stack,
  endpoint,
}: StackRedeployGitFormProps) {
  const router = useRouter();
  const [state, setState] = useState(defaultState);

  const [formValues, setFormValues] = useState<StackRedeployGitFormModel>({
    RepositoryURL: model.URL,
    RepositoryURLValid: true,
    ComposeFilePathInRepository: model.ConfigFilePath,
    RefName: model.ReferenceName,
    RepositoryAuthentication: false,
    RepositoryUsername: '',
    RepositoryPassword: '',
    RepositoryGitCredentialID: 0,
    SaveCredential: true,
    NewCredentialName: '',
    Env: stack.Env || [],
    Option: {
      Prune: Boolean(stack.Option?.Prune || false),
    },
    PullImage: false,
    SelectedRegistries: [],
    AutoUpdate: parseAutoUpdateResponse(stack.AutoUpdate),
    TLSSkipVerify: model.TLSSkipVerify,
  });

  const [savedFormValues, setSavedFormValues] =
    useState<StackRedeployGitFormModel>({ ...formValues });

  const mapStackRegistries = useCallback(
    (stackRegistryIds: number[], allRegistries: Registry[]) => {
      if (!stackRegistryIds || !allRegistries) {
        return [];
      }
      return compact(
        stackRegistryIds.map((id) => allRegistries.find(({ Id }) => Id === id))
      );
    },
    []
  );

  // Use the existing useEnvironmentRegistries hook to get endpoint-specific registries
  const registriesQuery = useEnvironmentRegistries(stack.EndpointId, {
    enabled: !!stack.EndpointId,
    hideDefault: true,
  });

  // Use the new git stack mutation hooks
  const updateGitStackMutation = useUpdateGitStack(stack.Id, stack.EndpointId);
  const updateGitStackSettingsMutation = useUpdateGitStackSettings();

  // Initialize form values from stack data
  const initializeFormValues = useCallback(() => {
    // Extract webhook ID first - only generate if not already set
    setState((prev) => {
      let { webhookId } = prev;
      if (!webhookId) {
        webhookId = createWebhookId();
        if (stack.AutoUpdate?.Webhook) {
          // Extract UUID from webhook URL if it's a full URL
          webhookId = stack.AutoUpdate.Webhook;
          if (webhookId.includes('/')) {
            // Extract the last part of the URL which should be the UUID
            const parts = webhookId.split('/');
            webhookId = parts[parts.length - 1] || webhookId;
          }
        }
      }
      return { ...prev, webhookId };
    });

    const newFormValues = {
      RepositoryURL: model.URL,
      RepositoryURLValid: true,
      ComposeFilePathInRepository: model.ConfigFilePath,
      RefName: model.ReferenceName,
      RepositoryAuthentication: false,
      RepositoryUsername: '',
      RepositoryPassword: '',
      RepositoryGitCredentialID: 0,
      SaveCredential: true,
      NewCredentialName: '',
      Env: stack.Env || [],
      Option: {
        Prune: Boolean(stack.Option?.Prune || false),
      },
      PullImage: false,
      SelectedRegistries: [], // This will be populated by mapStackRegistries after registries load
      AutoUpdate: parseAutoUpdateResponse(stack.AutoUpdate),
      TLSSkipVerify: model.TLSSkipVerify,
    };

    if (stack.GitConfig?.Authentication) {
      const auth = stack.GitConfig.Authentication;
      newFormValues.RepositoryUsername = auth.Username || '';
      newFormValues.RepositoryPassword = auth.Password || '';
      newFormValues.RepositoryAuthentication = true;

      if (auth.GitCredentialID && auth.GitCredentialID > 0) {
        newFormValues.SaveCredential = false;
        newFormValues.RepositoryGitCredentialID = auth.GitCredentialID;
      }
    }

    setFormValues(newFormValues);
    setSavedFormValues({ ...newFormValues });
  }, [model, stack]);

  // Initialize form values from stack data
  useEffect(() => {
    initializeFormValues();
  }, [initializeFormValues]);

  // Map stack registries when registries are loaded
  useEffect(() => {
    if (
      registriesQuery.data &&
      stack.Registries &&
      stack.Registries.length > 0
    ) {
      const mappedRegistries = mapStackRegistries(
        stack.Registries,
        registriesQuery.data
      );
      if (mappedRegistries.length > 0) {
        setFormValues((prev) => ({
          ...prev,
          SelectedRegistries: mappedRegistries,
        }));
        setSavedFormValues((prev) => ({
          ...prev,
          SelectedRegistries: mappedRegistries,
        }));
      }
    }
  }, [registriesQuery.data, stack.Registries, mapStackRegistries]);

  const handleChange = useCallback(
    (partialValue: Partial<StackRedeployGitFormModel>) => {
      setFormValues((prev) => {
        const newValues = { ...prev, ...partialValue };
        const hasChanges =
          JSON.stringify(savedFormValues) !== JSON.stringify(newValues);
        setState((statePrev) => ({
          ...statePrev,
          hasUnsavedChanges: hasChanges,
        }));
        return newValues;
      });
    },
    [savedFormValues]
  );

  const handleChangeTLSSkipVerify = useCallback(
    async (value: boolean) => {
      if (model.TLSSkipVerify && !value) {
        const confirmed = await confirmEnableTLSVerify();
        if (!confirmed) {
          return;
        }
      }
      handleChange({ TLSSkipVerify: value });
    },
    [model.TLSSkipVerify, handleChange]
  );

  const handleChangeAutoUpdate = useCallback(
    (values: Partial<AutoUpdateModel>) => {
      setFormValues((prev) => {
        const newValues = {
          ...prev,
          AutoUpdate: {
            ...prev.AutoUpdate!,
            ...values,
          },
        };
        const hasChanges =
          JSON.stringify(savedFormValues) !== JSON.stringify(newValues);
        setState((statePrev) => ({
          ...statePrev,
          hasUnsavedChanges: hasChanges,
        }));
        return newValues;
      });
    },
    [savedFormValues]
  );

  const handleRegistriesChange = useCallback(
    (value: Registry[]) => {
      handleChange({ SelectedRegistries: value });
    },
    [handleChange]
  );

  const handleSubmit = useCallback(async () => {
    const isSwarmStack = stack.Type === 1;
    const result = await confirmStackUpdate(
      'Any changes to this stack or application made locally in Portainer will be overridden, which may cause service interruption. Do you wish to continue?',
      isSwarmStack
    );

    if (!result) {
      return;
    }

    try {
      setState((prev) => ({ ...prev, redeployInProgress: true }));

      const payload: GitStackPayload = {
        env: formValues.Env,
        prune: formValues.Option.Prune,
        RepositoryReferenceName: formValues.RefName,
        RepositoryAuthentication: formValues.RepositoryAuthentication,
        RepositoryGitCredentialID: formValues.RepositoryGitCredentialID,
        RepositoryUsername: formValues.RepositoryUsername,
        RepositoryPassword: formValues.RepositoryPassword,
        RepositoryAuthorizationType: formValues.RepositoryAuthorizationType,
        PullImage: result.pullImage,
      };

      if (
        formValues.SelectedRegistries &&
        formValues.SelectedRegistries.length > 0
      ) {
        payload.Registries = formValues.SelectedRegistries.map(
          (registry) => registry.Id
        );
      }

      await updateGitStackMutation.mutateAsync(payload);

      notifySuccess('Success', 'Pulled and redeployed stack successfully');
      router.stateService.reload();
    } catch (err) {
      notifyError('Failure', err as Error, 'Failed redeploying stack');
    } finally {
      setState((prev) => ({ ...prev, redeployInProgress: false }));
    }
  }, [
    stack.Type,
    formValues.Env,
    formValues.Option.Prune,
    formValues.RefName,
    formValues.RepositoryAuthentication,
    formValues.RepositoryGitCredentialID,
    formValues.RepositoryUsername,
    formValues.RepositoryPassword,
    formValues.RepositoryAuthorizationType,
    formValues.SelectedRegistries,
    updateGitStackMutation,
    router.stateService,
  ]);

  const handleSaveSettings = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, inProgress: true }));

      const autoUpdate = transformAutoUpdateViewModel(
        formValues.AutoUpdate,
        state.webhookId
      );
      const payload: GitStackPayload = {
        AutoUpdate: autoUpdate,
        env: formValues.Env,
        RepositoryReferenceName: formValues.RefName,
        RepositoryAuthentication: formValues.RepositoryAuthentication,
        RepositoryGitCredentialID: formValues.RepositoryGitCredentialID,
        RepositoryUsername: formValues.RepositoryUsername,
        RepositoryPassword: formValues.RepositoryPassword,
        RepositoryAuthorizationType: formValues.RepositoryAuthorizationType,
        prune: formValues.Option.Prune,
        TLSSkipVerify: formValues.TLSSkipVerify,
      };

      if (
        formValues.SelectedRegistries &&
        formValues.SelectedRegistries.length > 0
      ) {
        payload.Registries = formValues.SelectedRegistries.map(
          (registry) => registry.Id
        );
      }

      await updateGitStackSettingsMutation.mutateAsync({
        stackId: stack.Id,
        endpointId: stack.EndpointId,
        payload,
      });

      notifySuccess('Success', 'Save stack settings successfully');

      setSavedFormValues(JSON.parse(JSON.stringify(formValues)));
      setState((prev) => ({
        ...prev,
        hasUnsavedChanges: false,
        inProgress: false,
      }));
    } catch (err) {
      notifyError('Failure', err as Error, 'Unable to save stack settings');
      setState((prev) => ({ ...prev, inProgress: false }));
    }
  }, [
    formValues,
    state.webhookId,
    stack.Id,
    stack.EndpointId,
    updateGitStackSettingsMutation,
  ]);

  function disableSaveSettingsButton(): boolean {
    return Boolean(
      state.inProgress ||
        state.redeployInProgress ||
        !state.hasUnsavedChanges ||
        (formValues.RepositoryAuthentication &&
          !formValues.RepositoryPassword &&
          formValues.RepositoryGitCredentialID === 0) ||
        (formValues.RepositoryAuthentication &&
          formValues.RepositoryPassword &&
          formValues.SaveCredential &&
          !formValues.NewCredentialName)
    );
  }

  return (
    <div className="form-horizontal my-8">
      <FormSection title="Redeploy from git repository">
        <InfoPanel
          className="text-muted small"
          url={formValues.RepositoryURL}
          type="stack"
          configFilePath={formValues.ComposeFilePathInRepository}
          additionalFiles={stack.AdditionalFiles}
          commitHash={model.ConfigHash}
        />

        <AutoUpdateFieldset
          value={formValues.AutoUpdate!}
          onChange={handleChangeAutoUpdate}
          environmentType="DOCKER"
          isForcePullVisible={stack.Type !== 3}
          baseWebhookUrl={state.baseWebhookUrl}
          webhookId={state.webhookId}
          webhooksDocs="/user/docker/stacks/webhooks"
        />

        <TimeWindowDisplay />

        <div className="form-group">
          <div className="col-sm-12">
            <Button
              color="none"
              onClick={() =>
                setState((prev) => ({ ...prev, showConfig: !prev.showConfig }))
              }
              data-cy="advanced-configuration-toggle-button"
            >
              <Icon
                icon={state.showConfig ? 'minus' : 'plus'}
                className="mr-1"
              />
              {state.showConfig ? 'Hide' : 'Advanced'} configuration
            </Button>
          </div>
        </div>

        {state.showConfig && (
          <>
            <RefField
              value={formValues.RefName || ''}
              onChange={(value: string) => handleChange({ RefName: value })}
              model={formValues}
              isUrlValid
              stackId={stack.Id}
            />

            <AuthFieldset
              value={formValues}
              onChange={(values: Partial<GitFormModel>) => handleChange(values)}
              isAuthExplanationVisible
            />

            <div className="form-group">
              <div className="col-sm-12">
                <SwitchField
                  name="TLSSkipVerify"
                  checked={formValues.TLSSkipVerify || false}
                  tooltip="Enabling this will allow skipping TLS validation for any self-signed certificate."
                  labelClass="col-sm-3 col-lg-2"
                  label="Skip TLS Verification"
                  onChange={handleChangeTLSSkipVerify}
                  data-cy="gitops-skip-tls-verification-switch"
                />
              </div>
            </div>

            <RelativePathFieldset
              values={stack as unknown as RelativePathModel}
              gitModel={formValues}
              isEditing
              hideEdgeConfigs
              onChange={() => {}}
            />
          </>
        )}

        <StackEnvironmentVariablesPanel
          values={formValues.Env}
          onChange={(value: Array<{ name: string; value: string }>) =>
            handleChange({ Env: value })
          }
          showHelpMessage
          isFoldable
        />

        <FormSection title="Registries">
          {registriesQuery.isLoading ? (
            <span className="small text-muted">Loading registries...</span>
          ) : (
            <div className="form-group">
              <label
                className="col-sm-3 col-lg-2 control-label text-left"
                htmlFor="registries-selector"
              >
                Select Registries
                <Tooltip message="Defaults to all registries. Select specific registry when using multiple registries from same provider to avoid Docker credential conflicts." />
              </label>
              <div className="col-sm-9 col-lg-4">
                {!registriesQuery.data || registriesQuery.data.length === 0 ? (
                  <span className="small text-muted">
                    No registries available. Please configure registries first.
                  </span>
                ) : (
                  <MultiRegistrySelectFieldset
                    options={registriesQuery.data}
                    value={formValues.SelectedRegistries}
                    onChange={handleRegistriesChange}
                    placeholder="Choose one or more registries for this stack"
                    showFormControl={false}
                    showEmptyState={false}
                  />
                )}
              </div>
            </div>
          )}
        </FormSection>

        {stack.Type === 1 && endpoint.apiVersion >= 1.27 && (
          <FormSection title="Options">
            <div className="form-group">
              <div className="col-sm-12">
                <SwitchField
                  name="prune"
                  checked={formValues.Option.Prune || false}
                  tooltip="Prune services that are no longer referenced."
                  labelClass="col-sm-3 col-lg-2"
                  label="Prune services"
                  onChange={(value: boolean) =>
                    handleChange({ Option: { Prune: value } })
                  }
                  data-cy="stack-prune-services-switch"
                />
              </div>
            </div>
          </FormSection>
        )}

        <FormSection title="Actions">
          <LoadingButton
            size="small"
            color="primary"
            onClick={handleSubmit}
            disabled={
              state.inProgress ||
              state.redeployInProgress ||
              state.hasUnsavedChanges
            }
            isLoading={state.redeployInProgress}
            loadingText="In progress..."
            data-cy="stack-redeploy-button"
          >
            <RefreshCw className="mr-1" />
            Pull and redeploy
          </LoadingButton>

          <LoadingButton
            size="small"
            color="primary"
            onClick={handleSaveSettings}
            disabled={disableSaveSettingsButton()}
            isLoading={state.inProgress}
            loadingText="In progress..."
            className="ml-2"
            data-cy="stack-save-settings-button"
          >
            Save settings
          </LoadingButton>
        </FormSection>
      </FormSection>
    </div>
  );
}
